import {
  Box,
  Button,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { IResponseCCS0001 } from '../../../../shared/services/types/response';
import moment from 'moment';
import Cookies from 'js-cookie';
import { useState } from 'react';
import { maskCPFCNPJ } from '../../../../shared/utils/mask';
import { api } from '../../../../shared/services/api';
import { toast } from 'react-toastify';
import { AxiosError, AxiosRequestConfig } from 'axios';
import ConfirmGenerateResponseModal from '../../../../shared/components/confirmGenerateResponseModal';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  height: '80%',
  bgcolor: 'background.paper',
  border: '2px solid #cecece',
  borderRadius: 5,
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

interface ModalDetailProps {
  ccsValue: IResponseCCS0001 | null;
  closeModal: (shouldReload?: boolean) => void;
  open: boolean;
}

const ModalDetail = ({ open, ccsValue, closeModal }: ModalDetailProps) => {
  const token = Cookies.get('@token');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [generateIsLoading, setGenerateIsLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const handleConfirmGenerate = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
      },
      responseType: 'blob',
    };

    setGenerateIsLoading(true);
    try {
      const responseGenerateDetailPerson = await api.get(
        `/archive/response-detail/${ccsValue?.id}`,
        config,
      );
      if (responseGenerateDetailPerson.status === 200) {
        const blob = new Blob([responseGenerateDetailPerson.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `CCS0002${ccsValue?.number_control_ccs}.zip`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Arquivo gerado com sucesso');
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.error || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setGenerateIsLoading(false);
      setConfirmModalOpen(false);
    }
    handleClose(true);
  };

  const resetState = () => {
    setPage(0);
    setRowsPerPage(5);
    setGenerateIsLoading(false);
  };

  const handleClose = (shouldReload?: boolean) => {
    resetState();
    closeModal(shouldReload);
  };

  const linkedPersonGroup = ccsValue?.participant_group_id || [];
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, linkedPersonGroup.length - page * rowsPerPage);

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box sx={{ ...style }}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">Detalhes do Arquivo CCS0001:</Typography>
          <Typography ml={1} color="primary.main" variant="h6">
            {ccsValue?.number_control_ccs}
          </Typography>
        </Box>
        <Typography variant="body1">
          Data de envio Bacen:
          {moment(ccsValue?.date_time_bacen).format('DD/MM/yyyy')}
        </Typography>

        <Box
          mt={6}
          component="form"
          sx={{ '& > :not(style)': { m: 1 } }}
          noValidate
          autoComplete="off"
        >
          {ccsValue?.partner_name && (
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Parceiro:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {ccsValue?.partner_name}
              </Typography>
            </Box>
          )}
          <Typography variant="body1">Vínculos</Typography>
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>CNPJ/CPF Vinculado</TableCell>
                  <TableCell>Nome da Pessoa Vinculada</TableCell>
                  <TableCell>Conta</TableCell>
                  <TableCell>Data de Início</TableCell>
                  <TableCell>Data de Fim</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  ccsValue?.participant_group_id?.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  ) || []
                ).map((person, key) => (
                  <TableRow key={key}>
                    <TableCell>
                      {maskCPFCNPJ(person.cnpj_cpf_participant)}
                    </TableCell>
                    <TableCell>{person.person_name}</TableCell>
                    <TableCell>{`${person.account_client}-${person.digit_account}`}</TableCell>
                    <TableCell>
                      {moment(person.start_date).format('DD/MM/yyyy')}
                    </TableCell>
                    <TableCell>
                      {person.end_date
                        ? moment(person.end_date).format('DD/MM/yyyy')
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5]}
              component="div"
              count={linkedPersonGroup.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            //onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'absolute',
            bottom: 16,
            right: 16,
          }}
        >
          {ccsValue?.status != 'DONE' && (
            <Button
              variant="contained"
              color="primary"
              disabled={generateIsLoading}
              onClick={handleOpenConfirmModal}
            >
              Gerar Resposta
            </Button>
          )}
          <Button
            sx={{ marginLeft: 2 }}
            variant="contained"
            color="error"
            onClick={() => handleClose(false)}
          >
            Cancelar
          </Button>
        </Box>
        <ConfirmGenerateResponseModal
          open={confirmModalOpen}
          isLoading={generateIsLoading}
          onConfirm={handleConfirmGenerate}
          onCancel={handleCloseConfirmModal}
        />
      </Box>
    </Modal>
  );
};

export default ModalDetail;
